import { FormControlNames, FormName, ScreenType } from '../../models/enums.models';
import { NavigationStart, Router, Event } from '@angular/router';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { Component, OnDestroy } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-screen-base',
    standalone: true,
    imports: [],
    templateUrl: './screen-base.component.html',
    styleUrl: './screen-base.component.css'
})

export class ScreenBaseComponent implements OnDestroy {
    _screen: ScreenType = ScreenType.Personal;
    _formNames = FormName;
    _fcn = FormControlNames;

    private _isNavigating: boolean = false;
    private _destroy$ = new Subject<void>();
    
    constructor(
        protected router: Router,
        protected type: ScreenType,
        protected toastr: ToastrService,
        protected stateService: StateService,
        protected claimsService: ClaimsService
    ) {
        this._screen = type;
        const typeStr = ScreenType[type];

        this.stateService.cachedClaim = JSON.stringify(this.stateService.claim);
        this.stateService.cachedCustomer = JSON.stringify(this.stateService.customer);

        router.events.pipe(takeUntil(this._destroy$)).subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
                const targetUrl = event.url;

                if (targetUrl === `/form/${typeStr}` && !this._isNavigating) {
                    this._isNavigating = true;
                    this.canMoveToNextRoute()
                        .then(() => {
                            this._isNavigating = false;
                        })
                        .catch(() => {
                            this._isNavigating = false;
                            if (router.url !== `/form/${typeStr}`) {
                                router.navigate(['form', typeStr]);
                            }
                        });
                }
            }
        });
    }

    //Clean up subscriptions, important for msal guard to work
    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    //Determines if the user can navigate to the next route
    //Validates the current screen and attempts to save the claim if necessary
    canMoveToNextRoute(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!this.isScreenValid()) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                reject();
                return;
            }

            if (this.hasStateChanged()) {
                this.saveClaimAndCustomer()
                    .then(() => {
                        this.updateCachedState();
                        resolve();
                    })
                    .catch(reject);
            } 
            else {
                resolve();
            }
        });
    }
    
    //Validates the current screen. This method should be overridden in child components
    isScreenValid(): boolean {
        console.warn('isScreenValid() is not overridden in the child component.');
        return true;
    }

    protected hasStateChanged(): boolean {
        const currentClaim = JSON.stringify(this.stateService.claim);
        const currentCustomer = JSON.stringify(this.stateService.customer);

        if(currentClaim !== this.stateService.cachedClaim || currentCustomer !== this.stateService.cachedCustomer){
            this.stateService.enableSaving = true;
            return true;
        }
        else{
            this.stateService.enableSaving = false;
            return false;
        }
    }

    private updateCachedState(): void {
        this.stateService.cachedClaim = JSON.stringify(this.stateService.claim);
        this.stateService.cachedCustomer = JSON.stringify(this.stateService.customer);
    }
    
    //Navigates to the next screen
    onNext(): void {
        if (!this.isScreenValid()) {
            this.toastr.warning('Please fix the form errors before proceeding.');
            return;
        }
        
        const newScreen = this.after(this._screen);
        const screenAsString = ScreenType[newScreen];
        this.router.navigate(['form', screenAsString]);
    }

    onPrevious(caseReference?: string): void {
        if (caseReference) {
            const redirectUri = `/home/${caseReference}`;
            this.router.navigateByUrl(redirectUri);
        } 
        else {
            const newScreen = this.before(this._screen);
            const screenAsString = ScreenType[newScreen];
            this.router.navigate(['form', screenAsString]);
        }
    }

    //Navigates to a specific screen
    navigateTo(screen: ScreenType): void {
        const screenAsString = ScreenType[screen];
        this.router.navigate(['form', screenAsString]);
    }

    //Calculates the next screen in the sequence
    after(value: ScreenType): ScreenType {
        const next = value + 1;
        return next in ScreenType ? next : value;
    }

    //Calculates the previous screen in the sequence
    before(value: ScreenType): ScreenType {
        const prev = value - 1;
        return prev in ScreenType ? prev : value;
    }
    
    protected saveClaimAndCustomer(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (this.stateService.enableSaving && !this.stateService.formIsSubmitted) {
                this.claimsService.saveClaim(this.stateService.claim!, this.stateService.customer!).subscribe({
                    next: (response) => {
                        this.stateService.claim!.claimUpdatedOn = response.claimUpdatedOn;
                        this.claimsService.saveCustomer(this.stateService.customer!).subscribe({
                            next: (customerResponse) => {
                                this.stateService.customer = customerResponse;
                                this.stateService.enableSaving = false;
                                resolve();
                            },
                            error: (error) => {
                                this.stateService.enableSaving = false;
                                console.error('Error saving customer:', error);
                                this.toastr.error('Error saving customer. Please try again.');
                                reject();
                            }
                        });
                    },
                    error: (error) => {
                        this.stateService.enableSaving = false;
                        console.error('Error saving claim:', error);
                        this.toastr.error('Error saving screen. Please try again.');
                        reject();
                    }
                });
            } else {
                resolve();
            }
        });
    }
}
